import { useState, useEffect } from "react";
import { Calendar as CalendarIcon, MapPin, Clock, Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addingEvent, setAddingEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    location: "",
    event_date: "",
    event_time: "",
    image_url: ""
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.event_date || !newEvent.event_time || !newEvent.location) {
      toast.error("Please fill in required fields");
      return;
    }

    setAddingEvent(true);
    try {
      const response = await axios.post(`${API}/events`, newEvent);
      setEvents([response.data, ...events]);
      setNewEvent({
        title: "",
        description: "",
        location: "",
        event_date: "",
        event_time: "",
        image_url: ""
      });
      setAddDialogOpen(false);
      toast.success("Event created successfully!");
    } catch (error) {
      console.error("Error adding event:", error);
      toast.error("Failed to create event");
    } finally {
      setAddingEvent(false);
    }
  };

  // Get event dates for calendar highlighting
  const eventDates = events.map(event => new Date(event.event_date));

  // Filter events for selected date
  const selectedDateStr = format(selectedDate, "yyyy-MM-dd");
  const filteredEvents = events.filter(event => event.event_date === selectedDateStr);

  // Upcoming events (sorted by date)
  const upcomingEvents = [...events]
    .filter(e => new Date(e.event_date) >= new Date())
    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

  return (
    <div data-testid="events-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Community Events
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
                Upcoming Events
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl">
                Join us at our upcoming events and be part of the movement for peace. From workshops to community forums, there's something for everyone.
              </p>
            </div>
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-event-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Event
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Outfit']">Create New Event</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddEvent} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="event-title">Event Title *</Label>
                    <Input
                      id="event-title"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                      placeholder="Enter event title"
                      className="mt-1"
                      required
                      data-testid="event-title-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="event-date">Date *</Label>
                      <Input
                        id="event-date"
                        type="date"
                        value={newEvent.event_date}
                        onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                        className="mt-1"
                        required
                        data-testid="event-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="event-time">Time *</Label>
                      <Input
                        id="event-time"
                        value={newEvent.event_time}
                        onChange={(e) => setNewEvent({ ...newEvent, event_time: e.target.value })}
                        placeholder="e.g., 2:00 PM"
                        className="mt-1"
                        required
                        data-testid="event-time-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="event-location">Location *</Label>
                    <Input
                      id="event-location"
                      value={newEvent.location}
                      onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                      placeholder="Enter event location"
                      className="mt-1"
                      required
                      data-testid="event-location-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="event-description">Description</Label>
                    <Textarea
                      id="event-description"
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                      placeholder="Describe the event"
                      className="mt-1 resize-none"
                      rows={3}
                      data-testid="event-description-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="event-image">Image URL (optional)</Label>
                    <Input
                      id="event-image"
                      value={newEvent.image_url}
                      onChange={(e) => setNewEvent({ ...newEvent, image_url: e.target.value })}
                      placeholder="https://..."
                      className="mt-1"
                      data-testid="event-image-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={addingEvent}
                    data-testid="submit-event-btn"
                  >
                    {addingEvent ? "Creating..." : "Create Event"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Calendar and Events */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sticky top-24">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-[#0056D2]" />
                  Event Calendar
                </h3>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-lg border-0"
                  modifiers={{
                    hasEvent: eventDates
                  }}
                  modifiersClassNames={{
                    hasEvent: "bg-[#0056D2]/10 text-[#0056D2] font-semibold"
                  }}
                  data-testid="events-calendar"
                />
                {filteredEvents.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-sm font-medium text-[#0F172A] mb-2">
                      {format(selectedDate, "MMMM d, yyyy")}
                    </p>
                    {filteredEvents.map(event => (
                      <div key={event.id} className="text-sm text-slate-600 mb-1">
                        • {event.title} at {event.event_time}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Events List */}
            <div className="lg:col-span-2">
              <h3 className="text-xl font-semibold text-[#0F172A] mb-6">Upcoming Events</h3>
              
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner w-10 h-10" />
                </div>
              ) : upcomingEvents.length > 0 ? (
                <div className="space-y-6">
                  {upcomingEvents.map((event) => {
                    const eventDate = new Date(event.event_date);
                    const month = format(eventDate, "MMM");
                    const day = format(eventDate, "d");
                    
                    return (
                      <div 
                        key={event.id} 
                        className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow duration-300"
                        data-testid={`event-card-${event.id}`}
                      >
                        <div className="flex flex-col md:flex-row">
                          {event.image_url && (
                            <div className="md:w-64 h-48 md:h-auto flex-shrink-0">
                              <img 
                                src={event.image_url} 
                                alt={event.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="flex-1 p-6">
                            <div className="flex gap-4">
                              <div className="date-badge flex-shrink-0">
                                <p className="date-badge-month">{month}</p>
                                <p className="date-badge-day">{day}</p>
                              </div>
                              <div className="flex-1">
                                <h4 className="text-xl font-semibold text-[#0F172A] mb-2">{event.title}</h4>
                                <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-3">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {event.event_time}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {event.location}
                                  </span>
                                </div>
                                <p className="text-slate-600 mb-4 line-clamp-2">{event.description}</p>
                                <Button variant="outline" className="btn-secondary text-sm py-2 px-4">
                                  Learn More
                                  <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16 bg-[#F0F9FF] rounded-3xl">
                  <CalendarIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-[#0F172A] mb-2">No Upcoming Events</h3>
                  <p className="text-slate-500 mb-6">Check back soon for new events!</p>
                  <Button onClick={() => setAddDialogOpen(true)} className="btn-primary">
                    <Plus className="w-5 h-5 mr-2" />
                    Create First Event
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
